/**
 * Burton Forms — pure validators + built-in rule registrations.
 * Layer 1 (no DOM). Country-aware rules (phone/postalCode) read the pattern from
 * `BAO.Forms.address` via ctx.country. Loads after burton-forms-core.js + burton-forms-address.js.
 */
(function registerBurtonFormsValidators () {
  const Forms = window.BAO.Forms

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const HAS_LETTER_RE = /\p{L}/u

  // Both transferred from the legacy SFCC `validationHelper.js`
  // eslint-disable-next-line no-misleading-character-class
  const NO_EMOJI_RE =
    /^((?!([\u2000-\u303f]|[\u3100-\u3300]|\u00a9|\u00ae|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])).)*$/
  const SUPPORTED_SCRIPT_RE =
    /^((?!([\u0391-\u0399]|[\u03b1-\u03b9]|[\u039a-\u039f]|[\u03ba-\u03bf]|\u03a0|\u03a1|[\u03a3-\u03a9]|[\u03c0-\u03c9]|[\u0410-\u0449]|[\u041a-\u041f]|\u042a|[\u043a-\u043f]|\u044a|\u044c|\u044e|\u044f)).)*$/

  // Pure predicates — each returns a boolean; the rule layer turns `false` into a message.
  const validators = {
    /** @param {string} value */
    required (value) {
      return typeof value === 'string'
        ? value.trim().length > 0
        : value != null
    },

    /** @param {string} value */
    email (value) {
      return EMAIL_RE.test(value)
    },

    /**
     * @param {string} value
     * @param {number} max
     */
    maxLength (value, max) {
      return String(value).length <= Number(max)
    },

    /**
     * Empty passes — an empty field is `required`'s problem, not this rule's, so an optional field
     * with a minimum doesn't error until the user actually types something.
     *
     * @param {string} value
     * @param {number} min
     */
    minLength (value, min) {
      return value === '' || String(value).length >= Number(min)
    },

    /**
     * @param {string} value
     * @param {RegExp} regExp
     */
    pattern (value, regExp) {
      return regExp.test(value)
    },

    /**
     * Country-aware. Passes (permissively) when the country has no phone pattern seeded yet.
     *
     * @param {string} value
     * @param {string|null} country
     */
    phone (value, country) {
      const config = countryConfig(country)
      const pattern = config && config.phone && config.phone.pattern

      return pattern ? pattern.test(value) : true
    },

    /**
     * Country-aware. Passes (permissively) when the country has no postal pattern seeded yet.
     *
     * @param {string} value
     * @param {string|null} country
     */
    postalCode (value, country) {
      const config = countryConfig(country)
      const pattern = config && config.postal && config.postal.pattern

      return pattern ? pattern.test(value) : true
    },

    /**
     * Modest calendar-date check (a real date that Date can parse). Regional display formats
     * (dd.mm.yyyy etc.) arrive with the warranty ticket.
     *
     * @param {string} value
     */
    date (value) {
      if (!value) return false

      const timestamp = Date.parse(value)

      return !Number.isNaN(timestamp)
    },

    /**
     * Combines the legacy SFCC `emojis` and `invalidAlphabets` checks, which were always together.
     *
     * @param {string} value
     */
    supportedChars (value) {
      return NO_EMOJI_RE.test(value) && SUPPORTED_SCRIPT_RE.test(value)
    },

    /**
     * At least one letter somewhere — rejects all-symbol ("$&@$(@") and all-digit ("12345") input.
     * @param {string} value
     */
    hasLetter (value) {
      return value === '' || HAS_LETTER_RE.test(value)
    },
  }

  /**
   * @param {string|null} country  ISO country code (address is country-keyed)
   * @returns {Object|null}
   */
  function countryConfig (country) {
    if (!country) return null

    return (Forms.address && Forms.address[country.toLowerCase()]) || null
  }

  Forms.validators = Forms.validators || validators

  // ---------------------------------------------------------------------------------------------
  // Built-in rule messages
  // ---------------------------------------------------------------------------------------------
  // ⚠️ STATUS: shipped as best-effort copy, pending REGIONAL QA.
  const builtIns = {
    required: {
      test: (value) => validators.required(value),
      message: {
        en: 'This field is required',
        fr: 'Ce champ est obligatoire',
        de: 'Dies ist ein Pflichtfeld',
        ja: '必須項目です',
      },
    },
    email: {
      test: (value) => validators.email(value),
      message: {
        en: 'Please enter a valid email address',
        fr: 'Veuillez saisir une adresse e-mail valide',
        'fr-CA': 'Veuillez saisir une adresse courriel valide',
        de: 'Bitte gib eine gültige E-Mail-Adresse ein',
        ja: '有効なメールアドレスを入力してください',
      },
    },
    phone: {
      test: (value, ctx) => validators.phone(value, ctx.country),
      message: {
        en: 'Please enter a valid phone number',
        fr: 'Veuillez saisir un numéro de téléphone valide',
        de: 'Bitte gib eine gültige Telefonnummer ein',
        ja: '有効な電話番号を入力してください',
      },
    },
    postalCode: {
      test: (value, ctx) => validators.postalCode(value, ctx.country),
      message: {
        en: 'Please enter a valid postal code',
        'en-US': 'Please enter a valid ZIP code',
        fr: 'Veuillez saisir un code postal valide',
        de: 'Bitte gib eine gültige Postleitzahl ein',
        ja: '有効な郵便番号を入力してください',
      },
    },
    date: {
      test: (value) => validators.date(value),
      message: {
        en: 'Please enter a valid date',
        fr: 'Veuillez saisir une date valide',
        de: 'Bitte gib ein gültiges Datum ein',
        ja: '有効な日付を入力してください',
      },
    },
    maxLength: {
      test: (value, ctx) => validators.maxLength(value, ctx.params[0]),
      message: {
        en: (ctx) => `Please use ${ctx.params[0]} characters or fewer`,
        fr: (ctx) => `Limite de ${ctx.params[0]} caractères`,
        de: (ctx) => `Bitte verwende maximal ${ctx.params[0]} Zeichen`,
        ja: (ctx) => `${ctx.params[0]}文字以内で入力してください`,
      },
    },
    pattern: {
      test: (value, ctx) =>
        validators.pattern(value, new RegExp(ctx.params.join(':'))),
      message: {
        en: 'Invalid format',
        fr: 'Format invalide',
        'fr-CA': 'Le format n’est pas valide',
        de: 'Ungültiges Format',
        ja: '形式が正しくありません',
      },
    },
    minLength: {
      test: (value, ctx) => validators.minLength(value, ctx.params[0]),
      message: {
        en: (ctx) => `Please use at least ${ctx.params[0]} characters`,
        fr: (ctx) => `Minimum de ${ctx.params[0]} caractères`,
        de: (ctx) => `Bitte verwende mindestens ${ctx.params[0]} Zeichen`,
        ja: (ctx) => `${ctx.params[0]}文字以上で入力してください`,
      },
    },
    hasLetter: {
      test: (value) => validators.hasLetter(value),
      message: {
        en: 'Please include at least one letter',
        fr: 'Veuillez inclure au moins une lettre',
        de: 'Bitte gib mindestens einen Buchstaben ein',
        ja: '文字を1つ以上入力してください',
      },
    },
    supportedChars: {
      test: (value) => validators.supportedChars(value),
      message: {
        en: 'Please remove unsupported characters',
        fr: 'Veuillez retirer les caractères non pris en charge',
        de: 'Bitte entferne nicht unterstützte Zeichen',
        ja: 'サポートされていない文字が含まれています',
      },
    },
  }

  Object.entries(builtIns).forEach(([name, definition]) => {
    Forms.rules.register(name, definition, { internal: true })
  })
})()
